import { useState } from 'react'
import DatePicker from 'react-datepicker'
import { useQuery } from '@tanstack/react-query'
import { appointmentsAPI } from '../../api'
import { useBooking } from '../../context/BookingContext'
import { RiArrowLeftLine, RiArrowRightLine, RiTimeLine } from 'react-icons/ri'
import { format, addDays } from 'date-fns'
import toast from 'react-hot-toast'
import 'react-datepicker/dist/react-datepicker.css'

export default function SelectDateTime() {
  const { booking, setDate, setTimeSlot, nextStep, prevStep } = useBooking()

  const { data: slots, isLoading } = useQuery({
    queryKey: ['slots', booking.date, booking.staff?.id],
    queryFn: () =>
      appointmentsAPI.getSlots({
        date: booking.date ? format(booking.date, 'yyyy-MM-dd') : null,
        staff_id: booking.staff?.id !== 'any' ? booking.staff?.id : undefined,
        service_ids: booking.services.map((s) => s.id).join(','),
      }),
    enabled: !!booking.date,
    select: (r) => r.data?.data || [],
  })

  const handleNext = () => {
    if (!booking.date)     { toast.error('Please select a date'); return }
    if (!booking.timeSlot) { toast.error('Please select a time slot'); return }
    nextStep()
  }

  return (
    <div>
      <h2 className="font-display text-salon-dark text-2xl font-bold mb-1">Pick Date & Time</h2>
      <p className="text-salon-muted font-body text-sm mb-6">Select your preferred appointment date and time slot.</p>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Date picker */}
        <div>
          <p className="input-label mb-3">Select Date</p>
          <DatePicker
            selected={booking.date}
            onChange={setDate}
            minDate={new Date()}
            maxDate={addDays(new Date(), 90)}
            inline
            calendarClassName="!border-0 !shadow-none w-full"
          />
        </div>

        {/* Time slots */}
        <div>
          <p className="input-label mb-3">
            Available Slots {booking.date && <span className="text-salon-muted font-normal">– {format(booking.date, 'dd MMM yyyy')}</span>}
          </p>

          {!booking.date ? (
            <div className="flex flex-col items-center justify-center h-40 text-salon-muted">
              <RiTimeLine className="text-4xl mb-2 text-salon-cream-dark" />
              <p className="text-sm font-body">Select a date first</p>
            </div>
          ) : isLoading ? (
            <div className="grid grid-cols-3 gap-2">
              {[...Array(9)].map((_, i) => <div key={i} className="skeleton h-10 rounded-xl" />)}
            </div>
          ) : slots?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-salon-muted">
              <p className="text-sm font-body">No slots available on this date.</p>
              <p className="text-xs font-body mt-1">Please try another date.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
              {slots.map((slot) => {
                const isSelected = booking.timeSlot === slot.time
                const isFull = !slot.available
                return (
                  <button
                    key={slot.time}
                    disabled={isFull}
                    onClick={() => !isFull && setTimeSlot(slot.time)}
                    className={`py-2 rounded-xl text-sm font-body font-medium transition-all duration-200 ${
                      isFull
                        ? 'bg-salon-cream text-salon-muted/40 cursor-not-allowed line-through'
                        : isSelected
                        ? 'bg-salon-primary text-white shadow-salon-sm'
                        : 'bg-salon-cream text-salon-dark hover:border-salon-primary hover:text-salon-primary border border-salon-cream-dark'
                    }`}
                  >
                    {slot.time}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button onClick={prevStep} className="btn-ghost"><RiArrowLeftLine /> Back</button>
        <button onClick={handleNext} className="btn-primary">Continue <RiArrowRightLine /></button>
      </div>
    </div>
  )
}
